import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, List, ListItem, ListItemText,
  CircularProgress, Stack, Chip, Typography, Box, TextField, MenuItem, Grid, Divider, Tooltip, Paper
} from '@mui/material';
import { Link as RouterLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import supabase from '../utils/supabase';
import SmartToyIcon from "@mui/icons-material/SmartToy";

const UpdateDialog = ({ open, onClose }) => {
  const { t, i18n } = useTranslation(["update_dialog", "home", "common"]);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [timeZone, setTimeZone] = useState("UTC");
  const [actorNameMap, setActorNameMap] = useState(new Map());
  const [page, setPage] = useState(0);
  const [pageSize] = useState(100);
  const [hasMore, setHasMore] = useState(false);
  const [summaryActions, setSummaryActions] = useState({});
  const [summaryTables, setSummaryTables] = useState({});
  const [totalCount, setTotalCount] = useState(0);
  const [taxonNameMap, setTaxonNameMap] = useState(new Map());

  // filters
  const [filterUserId, setFilterUserId] = useState("");
  const [filterAction, setFilterAction] = useState("");
  const [filterTable, setFilterTable] = useState("");
  const [filterFrom, setFilterFrom] = useState("");
  const [filterTo, setFilterTo] = useState("");
  // filter candidates
  const [candidateActors, setCandidateActors] = useState([]);
  const [candidateTables, setCandidateTables] = useState([]);
  const [candidateActions, setCandidateActions] = useState([]);

  const fetchServer = async (reset = false) => {
    setLoading(true);
    const targetPage = reset ? 0 : page;
    const offset = targetPage * pageSize;
    const { data, error } = await supabase.rpc("audit_log_query", {
      p_actor_id: filterUserId || null,
      p_table_name: filterTable || null,
      p_action: filterAction || null,
      p_from: filterFrom || null,
      p_to: filterTo ? `${filterTo}T23:59:59` : null,
      p_limit: pageSize,
      p_offset: offset,
    });
    if (error) {
      console.error("Error fetching audit_log_query", error);
      setLoading(false);
      return;
    }
    const payload = data?.[0] || {};
    const fetched = payload.logs || [];
    setSummaryActions(payload.counts_by_action || {});
    setSummaryTables(payload.counts_by_table || {});
    setTotalCount(payload.total_count || 0);
    if (reset) setLogs(fetched);
    else setLogs((prev) => [...prev, ...fetched]);
    setHasMore((fetched || []).length === pageSize);
    setPage(reset ? 1 : targetPage + 1);
    fetchActorNames(fetched);
    fetchTaxonNames(fetched);
    setLoading(false);
  };

  const fetchActorNames = async (candidateLogs) => {
    const existing = actorNameMap;
    const newIds = Array.from(
      new Set(
        candidateLogs
          .map((l) => l.actor_id)
          .filter((id) => id && !existing.has(id))
      )
    );
    if (!newIds.length) return;
    try {
      const members = await fetchMembersWithFallback(newIds);
      mergeMemberNames(members);
    } catch (err) {
      console.error("Error fetching members", err);
    }
  };

  const fetchPage = async (reset = false) => {
    setLoading(true);
    const targetPage = reset ? 0 : page;
    const from = targetPage * pageSize;
    const to = from + pageSize - 1;
    let data = null;
    let error = null;
    const primary = await supabase
      .from('audit_log')
      .select('*')
      .order('changed_at', { ascending: false })
      .range(from, to);
    if (primary.error && primary.error.code === "42703") {
      const fallback = await supabase
        .from('audit_log')
        .select('*')
        .order('created_at', { ascending: false })
        .range(from, to);
      data = fallback.data;
      error = fallback.error;
    } else {
      data = primary.data;
      error = primary.error;
    }
    if (error) {
      console.error('Error fetching audit logs:', error);
      setLoading(false);
      return;
    }
    const fetched = data || [];
    const merged = reset ? fetched : [...logs, ...fetched];
    setLogs(merged);
    setHasMore(fetched.length === pageSize);
    setPage(reset ? 1 : targetPage + 1);
    fetchActorNames(fetched);
    fetchTaxonNames(fetched);
    setLoading(false);
  };

  useEffect(() => {
    setTimeZone(Intl.DateTimeFormat().resolvedOptions().timeZone);
    if (open) {
      fetchServer(true);
      // preload filter candidates from server-side function (distinct across all logs)
      (async () => {
      const { data, error } = await supabase.rpc("audit_log_filter_candidates");
      if (error) {
        console.error("Error fetching filter candidates", error);
        return;
      }
        const row = data?.[0] || {};
        const actorIds = row.actor_ids || [];
        const actions = row.actions || [];
        const tables = row.tables || [];
        setCandidateActors(actorIds);
        setCandidateActions(actions);
        setCandidateTables(tables);

        try {
          const members = await fetchMembersWithFallback();
          mergeMemberNames(members);
        } catch (err) {
          console.error("Error preloading members", err);
        }
      })();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Ensure candidate actor IDs have names resolved via admin_team_members.actor_id/auth_user_id
  useEffect(() => {
    const missing = (candidateActors || []).filter((id) => id && !actorNameMap.has(id));
    if (!missing.length) return;
    (async () => {
      try {
        const members = await fetchMembersWithFallback(missing);
        mergeMemberNames(members);
      } catch (err) {
        console.error("Error fetching missing actor names", err);
      }
    })();
  }, [candidateActors, actorNameMap]);

  // Refetch when filters change (server-side filtered)
  useEffect(() => {
    if (open) {
      fetchServer(true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterUserId, filterAction, filterTable, filterFrom, filterTo]);

  // Helper: merge fetched members into actorNameMap (supports actor_id/auth_user_id/id)
  const mergeMemberNames = (members) => {
    if (!members) return;
    setActorNameMap((prev) => {
      const map = new Map(prev);
      (members || []).forEach((m) => {
        if (m.auth_user_id) map.set(m.auth_user_id, m.display_name);
        map.set(m.id, m.display_name);
      });
      return map;
    });
  };

  // Helper: preload scientific name display strings for taxon-linked logs
  const fetchTaxonNames = async (candidateLogs) => {
    const existing = taxonNameMap;
    const newIds = Array.from(
      new Set(
        (candidateLogs || []).map((l) => {
          const table = (l?.table_name || "").toLowerCase();
          if (table === "scientific_names") return l.row_id;
          if (table === "scientific_name_docs") {
            const after = parseJsonSafe(l?.after_data);
            const before = parseJsonSafe(l?.before_data);
            return after?.scientific_name_id || before?.scientific_name_id || l.row_id;
          }
          return null;
        }).filter((id) => id && !existing.has(id))
      )
    );
    if (!newIds.length) return;
    const { data, error } = await supabase
      .from("scientific_names")
      .select("id,name_spell_valid")
      .in("id", newIds);
    if (error) {
      console.error("Error fetching scientific_names", error);
      return;
    }
    const map = new Map(existing);
    (data || []).forEach((n) => map.set(n.id, n.name_spell_valid));
    setTaxonNameMap(map);
  };

  // Helper: fetch admin_team_members using auth_user_id (actor_id columnが無い環境でも安全)
  const fetchMembersWithFallback = async (ids = null) => {
    const hasIds = Array.isArray(ids) && ids.length > 0;
    const quoted = hasIds ? ids.map((id) => `"${id}"`).join(",") : "";
    let q = supabase.from("admin_team_members").select("id, auth_user_id, display_name");
    if (hasIds) {
      const orClause = `id.in.(${quoted}),auth_user_id.in.(${quoted})`;
      q = q.or(orClause);
    }
    const { data, error } = await q;
    if (error) throw error;
    return data;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "unknown date";
    const date = new Date(dateString);
    return `${date.toLocaleString()} (${timeZone})`;
  };

  const parseJsonSafe = (v) => {
    if (!v) return null;
    if (typeof v === "object") return v;
    try { return JSON.parse(v); } catch { return null; }
  };

  const formatAuditLabel = (log) => {
    const table = (log?.table_name || "").toLowerCase();
    const after = parseJsonSafe(log?.after_data);
    const before = parseJsonSafe(log?.before_data);
    if (table === "scientific_names") {
      const name = after?.name_spell_valid
        || before?.name_spell_valid
        || taxonNameMap.get(log?.row_id)
        || log?.row_id
        || t("update_dialog:audit.scientificName", "Scientific name");
      if (log.row_id) {
        return (
          <>
            {t("update_dialog:audit.scientificName", "Scientific name")}:{" "}
            <RouterLink to={`/taxonomy/taxon-explore?focus=${log.row_id}`} style={{ color: "#7FFFD4" }} onClick={onClose}>
              {name}
            </RouterLink>
          </>
        );
      }
      return `${t("update_dialog:audit.scientificName", "Scientific name")}: ${name}`;
    }
    if (table === "scientific_name_docs") {
      const doc = after || before || {};
      const taxonId = doc.scientific_name_id || log.row_id;
      const docType = doc.doc_type || t("update_dialog:audit.note", "Taxon note");
      const taxonName = taxonId ? taxonNameMap.get(taxonId) : null;
      const label = taxonName
        ? `${docType}: ${taxonName}`
        : taxonId
          ? `${docType}: ${taxonId}`
          : docType;
      if (taxonId) {
        return (
          <>
            {t("update_dialog:audit.note", "Taxon note")}:{" "}
            <RouterLink to={`/taxonomy/taxon-explore?focus=${encodeURIComponent(taxonId)}`} style={{ color: "#7FFFD4" }} onClick={onClose}>
              {label}
            </RouterLink>
          </>
        );
      }
      return `${t("update_dialog:audit.note", "Taxon note")}: ${label}`;
    }
    const actionText = displayActionLabel(log.action || t("update_dialog:audit.change", "Change"));
    const tableText = displayTableLabel(log.table_name) || t("update_dialog:audit.unknown", "unknown");
    return `${actionText} ${t("update_dialog:audit.on", "on")} ${tableText}${log.row_id ? ` (ID: ${log.row_id})` : ""}`;
  };

  const displayActionLabel = (raw) => {
    const key = (raw || "").toUpperCase();
    if (key === "DELETE") return t("update_dialog:audit.action.delete", "DELETE");
    if (key === "INSERT") return t("update_dialog:audit.action.insert", "INSERT");
    if (key === "UPDATE") return t("update_dialog:audit.action.update", "UPDATE");
    if (key === "ADD") return t("update_dialog:audit.action.add", "ADD");
    return key || "-";
  };

  const displayTableLabel = (table) => {
    if (!table) return t("update_dialog:tables.unknown", "unknown");
    const key = table.toLowerCase();
    const map = {
      scientific_names: t("update_dialog:tables.scientific_names", "Scientific names"),
      scientific_name_docs: t("update_dialog:tables.scientific_name_docs", "Taxon notes"),
      taxonomic_acts: t("update_dialog:tables.taxonomic_acts", "Taxonomic acts"),
      publications: t("update_dialog:tables.publications", "Publications"),
    };
    return map[key] || table;
  };

  const resolveActorName = (log) => {
    const pickDisplayName = (disp) => {
      if (!disp) return null;
      if (typeof disp === "object") {
        const lang = (i18n.language || "").split("-")[0];
        return disp[lang] || disp.en || Object.values(disp)[0];
      }
      return disp;
    };

    if (log.actor_id && actorNameMap.has(log.actor_id)) {
      return pickDisplayName(actorNameMap.get(log.actor_id));
    }
    return log.actor_display || t("update_dialog:audit.unknown", "unknown");
  };

  const userOptions = useMemo(() => {
    return (candidateActors || []).map((id) => {
      // prefer admin_team_members display name
      let label = actorNameMap.has(id) ? resolveActorName({ actor_id: id }) : null;
      if (!label) {
        const hit = logs.find((l) => l.actor_id === id);
        if (hit) label = resolveActorName(hit);
      }
      return { id, label: label || id };
    });
  }, [candidateActors, actorNameMap, logs]);

  const tableOptions = useMemo(() => {
    const set = new Set();
    logs.forEach((l) => set.add(l.table_name || "unknown"));
    return Array.from(set);
  }, [logs]);

  // logs are already server-filtered
  const filteredLogs = logs;
  const countsByTable = summaryTables || {};
  const countsByAction = summaryActions || {};

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>{t("update_dialog:dialogTitle", "All Data Updates")}</DialogTitle>
      <DialogContent>
        <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: (theme) => theme.palette.background.default }}>
          <Stack spacing={2}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3} lg={3}>
                <TextField
                  select
                  fullWidth
                  sx={{ minWidth: 160 }}
                  label={t("update_dialog:filter.user", "User")}
                  value={filterUserId}
                  onChange={(e) => setFilterUserId(e.target.value)}
                  size="small"
                >
                  <MenuItem value="">{t("update_dialog:filter.all", "(All)")}</MenuItem>
                  {userOptions.map((u) => (
                    <MenuItem key={u.id} value={u.id}>{u.label}</MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6} md={2} lg={2}>
                <TextField
                  select
                  fullWidth
                  sx={{ minWidth: 120 }}
                  label={t("update_dialog:filter.action", "Action")}
                  value={filterAction}
                  onChange={(e) => setFilterAction(e.target.value)}
                  size="small"
                >
                  <MenuItem value="">{t("update_dialog:filter.all", "(All)")}</MenuItem>
                  {(candidateActions.length ? candidateActions : ["INSERT","UPDATE","DELETE"]).map((act) => (
                    <MenuItem key={act} value={act}>{displayActionLabel(act)}</MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6} md={3} lg={3}>
                <TextField
                  select
                  fullWidth
                  sx={{ minWidth: 140 }}
                  label={t("update_dialog:filter.table", "Table")}
                  value={filterTable}
                  onChange={(e) => setFilterTable(e.target.value)}
                  size="small"
                >
                  <MenuItem value="">{t("update_dialog:filter.all", "(All)")}</MenuItem>
                  {(candidateTables.length ? candidateTables : tableOptions).map((tname) => (
                    <MenuItem key={tname} value={tname}>{displayTableLabel(tname)}</MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6} md={2} lg={2}>
                <TextField
                  fullWidth
                  sx={{ minWidth: 140 }}
                  label={t("update_dialog:filter.from", "From")}
                  type="date"
                  value={filterFrom}
                  onChange={(e) => setFilterFrom(e.target.value)}
                  size="small"
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2} lg={2}>
                <TextField
                  fullWidth
                  sx={{ minWidth: 140 }}
                  label={t("update_dialog:filter.to", "To")}
                  type="date"
                  value={filterTo}
                  onChange={(e) => setFilterTo(e.target.value)}
                  size="small"
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </Grid>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
              <Chip label={`${t("update_dialog:stats.hits", "Hits")}: ${filteredLogs.length} / ${totalCount}`} color="primary" size="small" />
              <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
              <Stack direction="row" spacing={1} flexWrap="wrap">
                {Object.entries(countsByAction).map(([k, v]) => (
                  <Chip key={k} label={`${displayActionLabel(k)}: ${v}`} size="small" variant="outlined" />
                ))}
              </Stack>
              <Stack direction="row" spacing={1} flexWrap="wrap">
                {Object.entries(countsByTable).map(([k, v]) => (
                  <Chip key={k} label={`${displayTableLabel(k)}: ${v}`} size="small" variant="outlined" />
                ))}
              </Stack>
            </Stack>
          </Stack>
        </Paper>

        {loading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : (
          <List>
            {filteredLogs.map((log, index) => {
              const when = log.changed_at || log.created_at;
              const who = resolveActorName(log);
              const isBot = who.toLowerCase().includes("bot");
              const whoLabel = (() => {
                if (!isBot) return who;
                if (who.toLowerCase().includes("hierarchical_import")) return t("update_dialog:bot.hierarchicalImport", "BOT: Hierarchical import");
                return who;
              })();
              const actionLabel = (log.action || "-").toUpperCase();
              const normalizedAction = actionLabel === "ADD" ? "INSERT" : actionLabel;
              const actionColor =
                normalizedAction === "INSERT" ? "success" :
                normalizedAction === "UPDATE" ? "warning" :
                normalizedAction === "DELETE" ? "error" : "default";

              return (
                <ListItem key={index} divider>
                  <ListItemText
                    primary={
                      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" mb={0.5}>
                        <Chip size="small" label={when ? formatDate(when) : t("update_dialog:audit.unknownDate", "Unknown date")} sx={{ height: 22 }} />
                        <Chip size="small" label={displayActionLabel(actionLabel)} color={actionColor} variant="outlined" sx={{ height: 22 }} />
                        <Chip size="small" label={whoLabel} color={isBot ? "default" : "primary"} icon={isBot ? <SmartToyIcon fontSize="small" /> : undefined} sx={{ height: 22, "& .MuiChip-icon": { ml: 0.2 } }} />
                      </Stack>
                    }
                    secondary={<Typography component="span" variant="body2" color="text.secondary">{formatAuditLabel(log)}</Typography>}
                  />
                </ListItem>
              );
            })}
            {hasMore && (
              <Box display="flex" justifyContent="center" py={2}>
                <Button variant="outlined" onClick={() => fetchPage(false)} disabled={loading}>
                  {t("update_dialog:list.loadMore", "Load more")}
                </Button>
              </Box>
            )}
          </List>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t("common:close", "Close")}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default UpdateDialog;
